import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Briefcase, Coins, MessageCircle, UserRound, History, MapPinned } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import GpsTracker from "@/components/GpsTracker";
import LockGuard from "@/components/LockScreen";
import { useRealtime } from "@/context/Realtime";

function LiveDot() {
  const { connected } = useRealtime();
  const { user } = useAuth();
  const online = connected && !!user.location;
  return (
    <span data-testid="realtime-status" title={online ? "GPS & realtime online" : "Menghubungkan GPS..."}
      className={`relative w-9 h-9 rounded-full grid place-items-center ${online ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
      <MapPinned className="w-4 h-4" />
      <span className={`absolute top-1 right-1 w-2.5 h-2.5 rounded-full border-2 border-white ${online ? "bg-emerald-500" : "bg-slate-300"}`}>
        {online && <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" />}
      </span>
    </span>
  );
}

function ModeToggle() {
  const { user, mode, setMode } = useAuth();
  const nav = useNavigate();
  const pick = (m) => {
    if (m === "worker" && !user.is_worker) {
      toast.info(user.kyc_status === "approved" ? "Daftar sebagai worker dulu" : "Selesaikan verifikasi KYC untuk menjadi worker");
      nav(user.kyc_status === "approved" ? "/profile/worker-register" : "/profile/kyc");
      return;
    }
    setMode(m);
    nav("/");
  };
  return (
    <div data-testid="header-role-toggle" className="relative inline-flex p-1 bg-slate-100 rounded-full border border-slate-200 shadow-inner">
      {[["user", "Pengguna"], ["worker", "Worker"]].map(([m, label]) => (
        <button key={m} data-testid={`${m}-mode-btn`} onClick={() => pick(m)}
          className={`relative z-10 px-3.5 py-1.5 text-xs font-bold rounded-full transition-colors ${mode === m ? "text-white" : "text-slate-600"}`}>
          {mode === m && (
            <motion.span layoutId="modepill" transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={`absolute inset-0 -z-10 rounded-full ${m === "user" ? "bg-emerald-600" : "bg-amber-500"}`} />
          )}
          {label}
        </button>
      ))}
    </div>
  );
}

function NavItem({ to, label, Icon, tid, active }) {
  return (
    <NavLink to={to} end={to === "/"} data-testid={tid}
      className={({ isActive }) => `relative flex-1 flex flex-col items-center gap-0.5 py-1.5 text-[10px] font-bold transition-colors ${isActive ? active : "text-slate-400 hover:text-slate-600"}`}>
      {({ isActive }) => (<>
        <Icon className={`w-5 h-5 transition-transform ${isActive ? "-translate-y-0.5 scale-110" : ""}`} />
        {label}
        {isActive && <motion.span layoutId="navdot" className="absolute -bottom-0.5 w-1 h-1 rounded-full bg-current" />}
      </>)}
    </NavLink>
  );
}

function BottomNav() {
  const { mode } = useAuth();
  const isW = mode === "worker";
  const active = isW ? "text-amber-600" : "text-emerald-600";
  const left = [["/", "Beranda", Home, "nav-home"], ["/jobs", isW ? "Riwayat" : "Pekerjaan", isW ? History : Briefcase, "nav-jobs"]];
  const right = [["/chat", "Chat", MessageCircle, "nav-chat"], ["/profile", "Profil", UserRound, "nav-profile"]];
  return (
    <nav className="fixed left-0 right-0 bottom-0 z-50 max-w-md mx-auto px-4 pb-safe pointer-events-none">
      <div className="pointer-events-auto relative flex items-end h-16 px-2 rounded-[1.75rem] bg-white/75 backdrop-blur-xl border border-white/60 shadow-[0_10px_40px_-10px_rgba(15,23,42,0.35)]">
        {left.map(([to, l, I, t]) => <NavItem key={to} to={to} label={l} Icon={I} tid={t} active={active} />)}
        <div className="flex-1 flex justify-center">
          <NavLink to="/coins" data-testid="nav-coins" aria-label="Koin"
            className={`-translate-y-6 w-16 h-16 rounded-full grid place-items-center text-white ring-[6px] ring-slate-50 shadow-xl transition-transform hover:scale-105 active:scale-95 bg-gradient-to-br ${isW ? "from-amber-400 via-orange-500 to-rose-500 shadow-orange-500/40" : "from-emerald-400 via-teal-500 to-cyan-600 shadow-emerald-500/40"}`}>
            <div className="flex flex-col items-center"><Coins className="w-6 h-6" /><span className="text-[9px] font-extrabold tracking-wider">KOIN</span></div>
          </NavLink>
        </div>
        {right.map(([to, l, I, t]) => <NavItem key={to} to={to} label={l} Icon={I} tid={t} active={active} />)}
      </div>
    </nav>
  );
}

export default function AppShell() {
  return (
    <div className="max-w-md mx-auto min-h-screen bg-slate-50 pb-36 relative shadow-2xl sm:border-x sm:border-slate-200">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200/60 px-4 h-14 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-2">
          <LiveDot />
          <NotificationBell />
          <ModeToggle />
        </div>
      </header>
      <main className="px-4 pt-4">
        <Outlet />
      </main>
      <BottomNav />
      <GpsTracker />
      <LockGuard />
    </div>
  );
}
